import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import {
  GridComponent,
  Page,
  Inject,
  ColumnDirective,
  ColumnsDirective,
  Filter,
  Sort,
} from "@syncfusion/ej2-react-grids";

import Spinner from "../Components/Spinner";
import ToastError from "../Components/ToastError";
import { AiFillDelete, AiFillEdit } from "react-icons/ai";

import { manufacturerService } from "../../Services/manufacturer.service";

const PAGE_TITLE = "Fabricante de Equipamentos";

export default function Manufacturers() {
  const dispatch = useDispatch();

  const [isLoading, setIsLoading] = useState(false);
  const [showError, setShowError] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const [manufacturers, setManufacturers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [formId, setFormId] = useState("");
  const [formName, setFormName] = useState("");

  useEffect(() => {
    dispatch({ type: "title", value: PAGE_TITLE });
  }, [dispatch]);

  const handleCloseError = () => {
    setShowError(false);
  };

  const clearSuccess = () => {
    setSuccessMessage("");
  };

  const normalizeManufacturer = (response) => ({
    manufacturerId: Number(response?.manufacturerId ?? response?.ManufacturerId ?? 0),
    manufacturerName: response?.manufacturerName ?? response?.ManufacturerName ?? "",
  });

  const loadManufacturers = async () => {
    clearSuccess();

    try {
      setIsLoading(true);

      // Chamada padrão solicitada: GET /manufacturer/0 ao entrar na tela.
      const initialResponse = await manufacturerService.getById(0);

      if (Array.isArray(initialResponse) && initialResponse.length > 0) {
        const normalized = initialResponse
          .map((item) => normalizeManufacturer(item))
          .filter((item) => item && item.manufacturerId && item.manufacturerName);

        setManufacturers(normalized);
        return;
      }

      if (initialResponse && !Array.isArray(initialResponse)) {
        const normalizedSingle = normalizeManufacturer(initialResponse);
        if (normalizedSingle?.manufacturerId && normalizedSingle?.manufacturerName) {
          setManufacturers([normalizedSingle]);
          return;
        }
      }

      // Usa a listagem atual apenas para obter os IDs e monta os dados finais pelo GET /manufacturer/{id}.
      const listResponse = await manufacturerService.list();
      const ids = (listResponse || [])
        .map((item) => item?.manufacturerId ?? item?.ManufacturerId ?? item?.automakerId)
        .filter((id) => id !== undefined && id !== null && id !== "");

      const uniqueIds = [...new Set(ids)];

      const details = await Promise.all(
        uniqueIds.map(async (id) => {
          const result = await manufacturerService.getById(id);
          return result ? normalizeManufacturer(result) : null;
        })
      );

      setManufacturers(
        details.filter((item) => item && item.manufacturerId && item.manufacturerName)
      );
    } catch (error) {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const sortedManufacturers = useMemo(
    () => [...manufacturers].sort((a, b) => a.manufacturerId - b.manufacturerId),
    [manufacturers]
  );

  const actionsTemplate = (item) => (
    <div className="d-flex gap-2">
      <AiFillEdit
        style={{ cursor: "pointer", color: "var(--text-primary)", fontSize: "16px" }}
        onClick={() => openEditForm(item)}
        title="Editar"
      />
      <AiFillDelete
        style={{ cursor: "pointer", color: "var(--text-primary)", fontSize: "16px" }}
        onClick={() => handleDelete(item)}
        title="Excluir"
      />
    </div>
  );

  const openCreateForm = () => {
    clearSuccess();
    setIsEditing(false);
    setFormId("");
    setFormName("");
    setShowForm(true);
  };

  const openEditForm = (item) => {
    clearSuccess();
    setIsEditing(true);
    setFormId(String(item.manufacturerId));
    setFormName(item.manufacturerName);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setIsEditing(false);
    setFormId("");
    setFormName("");
  };

  const handleCreate = async () => {
    clearSuccess();

    if (!formName.trim()) return;

    try {
      setIsLoading(true);

      const response = await manufacturerService.createManufacturer({
        ManufacturerName: formName.trim(),
      });

      if (!response) {
        setShowError(true);
        return;
      }

      const createdId =
        response?.manufacturerId ?? response?.ManufacturerId ?? response?.id;

      setSuccessMessage(
        `Fabricante criado com sucesso${createdId ? ` (ID ${createdId})` : ""}.`
      );

      await loadManufacturers();
      closeForm();
    } catch (error) {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdate = async () => {
    clearSuccess();

    if (!formId || !formName.trim()) return;

    try {
      setIsLoading(true);

      const response = await manufacturerService.updateManufacturer({
        ManufacturerId: Number(formId),
        ManufacturerName: formName.trim(),
      });

      if (!response) {
        setShowError(true);
        return;
      }

      const updatedId =
        response?.manufacturerId ?? response?.ManufacturerId ?? formId;

      setSuccessMessage(
        `Fabricante atualizado com sucesso (ID ${updatedId}).`
      );

      await loadManufacturers();
      closeForm();
    } catch (error) {
      setShowError(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = (item) => {
    clearSuccess();
    setManufacturers((prev) =>
      prev.filter((x) => x.manufacturerId !== item.manufacturerId)
    );
    setSuccessMessage(
      `Fabricante ${item.manufacturerId} removido da tabela (sem exclusão na API).`
    );
  };

  useEffect(() => {
    loadManufacturers();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      {isLoading ? <Spinner /> : null}
      <ToastError show={showError} closeToast={handleCloseError} />

      {!showForm ? (
        <div className="card p-3" style={{ marginBottom: 16 }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h5 className="mb-0">Fabricantes</h5>
            <button className="btn btn-success" onClick={openCreateForm}>
              Adicionar
            </button>
          </div>

          <GridComponent
            dataSource={sortedManufacturers}
            allowPaging
            allowFiltering
            allowSorting
            filterSettings={{ type: "Menu" }}
            pageSettings={{ pageSize: 15 }}
            rowHeight={30}
            height={"auto"}
            style={{ marginTop: 16 }}
            locale="pt-BR"
          >
            <ColumnsDirective>
              <ColumnDirective
                field="manufacturerName"
                headerText="ManufacturerName"
                width="420"
                textAlign="Left"
              />
              <ColumnDirective
                headerText="Ações"
                width="120"
                template={actionsTemplate}
                allowFiltering={false}
                allowSorting={false}
              />
            </ColumnsDirective>
            <Inject services={[Page, Filter, Sort]} />
          </GridComponent>
        </div>
      ) : (
        <div className="card p-3" style={{ marginBottom: 16 }}>
          <h5 className="mb-3">
            {isEditing ? "Editar fabricante" : "Adicionar fabricante"}
          </h5>

          <div className="row g-2 align-items-end">
            <div className="col-md-6">
              <label className="form-label">Nome do fabricante</label>
              <input
                type="text"
                className="form-control"
                value={formName}
                onChange={(e) => setFormName(e.target.value)}
                placeholder="Nome do fabricante"
              />
            </div>

            <div className="col-md-4 d-flex gap-2">
              <button className="btn btn-secondary" onClick={closeForm}>
                Cancelar
              </button>
              <button
                className={`btn ${isEditing ? "btn-warning" : "btn-success"}`}
                onClick={isEditing ? handleUpdate : handleCreate}
              >
                {isEditing ? "Salvar edição" : "Salvar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {successMessage ? (
        <div className="alert alert-success mt-3" role="alert">
          {successMessage}
        </div>
      ) : null}
    </div>
  );
}
